import React, { useMemo, useState } from 'react';
import { Alert, Input, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';

import { Button } from '~components';
import Icon, { faExclamationTriangle } from '~components/icon';

import { SahService } from '~lookups/models';

import { useGetSahServicesQuery } from '~lookups/actions/sahServices';

interface ExcludeServiceTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (serviceIds: string[]) => void;
  currentExclusions: string[];
  isSaving: boolean;
}

const ExcludeServiceTypeModal: React.FC<ExcludeServiceTypeModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  currentExclusions,
  isSaving,
}) => {
  const { data: sahServices = [] } = useGetSahServicesQuery({}, { skip: !isOpen });
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [search, setSearch] = useState('');

  const availableServices = useMemo(() => {
    return sahServices.filter((s: SahService) => !currentExclusions.includes(s.id));
  }, [sahServices, currentExclusions]);

  const filteredServices = useMemo(() => {
    if (!search.trim()) return availableServices;
    const term = search.toLowerCase();
    return availableServices.filter((s: SahService) => s.name.toLowerCase().includes(term));
  }, [availableServices, search]);

  const toggleService = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleSubmit = () => {
    onSubmit(selectedIds);
    setSelectedIds([]);
    setSearch('');
  };

  const handleClose = () => {
    setSelectedIds([]);
    setSearch('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg" className="exclude-service-type-modal">
      <ModalHeader toggle={handleClose}>Exclude Service Types</ModalHeader>
      <ModalBody className="p-4 d-flex flex-column gap-3">
        <Alert color="danger" className="d-flex align-items-start gap-2 mb-0">
          <Icon icon={faExclamationTriangle} className="mt-1" />
          <div>
            <strong>Warning:</strong> Excluding service types is a blanket restriction across all SaH features including
            Claims, Budgets, Supplier Invoices, Price Plans, and Appointment Imports. Excluded service types will be
            immediately unavailable for all clients.
          </div>
        </Alert>

        <Alert color="warning" className="d-flex align-items-start gap-2 mb-0">
          <Icon icon={faExclamationTriangle} className="mt-1" />
          <div>
            This action affects <strong>all existing and future</strong> data for this company. Ensure you understand
            the impact before proceeding.
          </div>
        </Alert>

        <Input
          type="text"
          placeholder="Search services..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <div className="service-list">
          {filteredServices.length === 0 ? (
            <div className="p-3 text-muted text-center">
              {search ? 'No matching services found' : 'No services available to exclude'}
            </div>
          ) : (
            filteredServices.map((service: SahService) => (
              <label
                key={service.id}
                className="d-flex align-items-center gap-2 px-3 py-2 border-bottom m-0 service-list-item"
              >
                <Input
                  type="checkbox"
                  checked={selectedIds.includes(service.id)}
                  onChange={() => toggleService(service.id)}
                  className="m-0"
                />
                <span>{service.name}</span>
              </label>
            ))
          )}
        </div>

        {selectedIds.length > 0 && (
          <div className="text-muted small">
            {selectedIds.length} service type{selectedIds.length !== 1 ? 's' : ''} selected for exclusion
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="success" disabled={selectedIds.length === 0} loading={isSaving} onClick={handleSubmit}>
          Exclude {selectedIds.length > 0 ? selectedIds.length : ''} Service Type{selectedIds.length !== 1 ? 's' : ''}
        </Button>
        <Button color="secondary" onClick={handleClose}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ExcludeServiceTypeModal;
